import { useState } from 'react';
import { Position } from '@/hooks/useUserPositions';
import { IncentiveKey } from '@/types';

// Compact incentive item
function IncentiveItem({
  incentive,
  isSelected,
  onClick,
}: {
  incentive: IncentiveKey;
  isSelected: boolean;
  onClick: () => void;
}) {
  const reward = (Number(incentive.reward) / 1e18).toLocaleString();
  const endDate = new Date(Number(incentive.endTime) * 1000).toLocaleDateString();

  return (
    <button
      onClick={onClick}
      className={`w-full p-2 rounded-lg border text-left transition-all text-sm ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-blue-300'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
              isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
            }`}
          >
            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
          </div>
          <span className="font-medium text-gray-900">{reward} JOCX</span>
        </div>
        <span className="text-xs text-gray-500">~{endDate}</span>
      </div>
    </button>
  );
}

interface PositionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  position: Position | null;
  isStaked?: boolean;
  incentive?: IncentiveKey;
  liveIncentives?: IncentiveKey[];
  unclaimedRewards?: string;
  jocxAmount?: string;
  usdtAmount?: string;
  isInRange?: boolean;
  onStake?: (selectedIncentive?: IncentiveKey) => Promise<void>;
  onUnstake?: () => Promise<void>;
  onClaim?: () => Promise<void>;
  onWithdraw?: () => Promise<void>;
  onRemoveLiquidity?: () => Promise<void>;
}

export function PositionDetailModal({
  isOpen,
  onClose,
  position,
  isStaked = false,
  incentive,
  liveIncentives = [],
  unclaimedRewards = '0',
  jocxAmount,
  usdtAmount,
  isInRange,
  onStake,
  onUnstake,
  onClaim,
  onWithdraw,
  onRemoveLiquidity,
}: PositionDetailModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [selectedIncentive, setSelectedIncentive] = useState<IncentiveKey | undefined>(incentive);

  if (!isOpen || !position) return null;

  const handleAction = async (action: string, handler?: () => Promise<void>) => {
    if (!handler) return;
    setIsProcessing(true);
    setProcessingAction(action);
    try {
      await handler();
      onClose();
    } catch (error) {
      console.error(`${action} failed:`, error);
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const isDeposited = position.staked === false && !isStaked;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={onClose}
      />

      {/* Modal - Compact design */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
          {/* Compact Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-1">
                <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-xs text-white font-bold border-2 border-white">J</div>
                <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center text-xs text-white font-bold border-2 border-white">U</div>
              </div>
              <div>
                <h2 className="text-base font-bold">#{position.tokenId}</h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Status badge inline */}
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isStaked ? 'bg-green-100 text-green-700' : isDeposited ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isStaked ? 'bg-green-500' : isDeposited ? 'bg-amber-500' : 'bg-blue-500'}`} />
                {isStaked ? 'Earning' : isDeposited ? 'Ready' : 'Wallet'}
              </span>
              {isInRange !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isInRange ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                  {isInRange ? 'In Range' : 'Out'}
                </span>
              )}
              <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Compact Content */}
          <div className="px-4 py-3 space-y-3">
            {/* Token amounts - inline */}
            {(jocxAmount || usdtAmount) && (
              <div className="flex gap-2">
                <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                  <div className="text-xs text-gray-500">JOCX</div>
                  <div className="text-base font-bold">
                    {parseFloat(jocxAmount || '0').toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                  <div className="text-xs text-gray-500">USDT</div>
                  <div className="text-base font-bold">
                    {parseFloat(usdtAmount || '0').toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            )}

            {/* Rewards - compact */}
            {(isStaked || parseFloat(unclaimedRewards) > 0) && (
              <div className="bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-500">{isStaked ? 'Rewards' : 'Earned'}</div>
                    <div className="text-base font-bold text-amber-600">{parseFloat(unclaimedRewards).toFixed(4)} JOCX</div>
                  </div>
                  {parseFloat(unclaimedRewards) > 0 && !isStaked && (
                    <button
                      onClick={() => handleAction('claim', onClaim)}
                      disabled={isProcessing}
                      className="px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50"
                    >
                      {processingAction === 'claim' ? '...' : 'Claim'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Incentive info - compact */}
            {isStaked && incentive ? (
              <div className="bg-green-50 rounded-lg px-3 py-2 border border-green-200">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-xs font-semibold text-green-700">Earning from</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{(Number(incentive.reward) / 1e18).toLocaleString()} JOCX Pool</span>
                  <span className="text-gray-500">~{new Date(Number(incentive.endTime) * 1000).toLocaleDateString()}</span>
                </div>
              </div>
            ) : liveIncentives.length > 0 ? (
              <div className="bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
                <div className="text-xs text-gray-500 mb-2">Select Program</div>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {liveIncentives.map((inc, idx) => (
                    <IncentiveItem
                      key={inc.id || idx}
                      incentive={inc}
                      isSelected={selectedIncentive?.id === inc.id}
                      onClick={() => setSelectedIncentive(inc)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* Compact Actions */}
          <div className="px-4 py-3 border-t border-gray-100 space-y-2">
            {isStaked ? (
              <button
                onClick={() => handleAction('unstake', onUnstake)}
                disabled={isProcessing}
                className="w-full py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                {processingAction === 'unstake' ? 'Processing...' : 'Unstake'}
              </button>
            ) : isDeposited ? (
              <>
                <button
                  onClick={() => handleAction('stake', async () => { if (onStake) await onStake(selectedIncentive); })}
                  disabled={isProcessing || !selectedIncentive}
                  className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {processingAction === 'stake' ? 'Processing...' : 'Stake'}
                </button>
                <button
                  onClick={() => handleAction('withdraw', onWithdraw)}
                  disabled={isProcessing}
                  className="w-full py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 text-sm"
                >
                  {processingAction === 'withdraw' ? 'Processing...' : 'Withdraw'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleAction('stake', async () => { if (onStake) await onStake(selectedIncentive); })}
                  disabled={isProcessing || !selectedIncentive}
                  className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {processingAction === 'stake' ? 'Processing...' : 'Transfer & Stake'}
                </button>
                {onRemoveLiquidity && (
                  <button
                    onClick={() => handleAction('remove', onRemoveLiquidity)}
                    disabled={isProcessing}
                    className="w-full py-2.5 border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50 disabled:opacity-50 text-sm"
                  >
                    {processingAction === 'remove' ? 'Processing...' : 'Remove Liquidity'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
